/**
 * Visual-regression test-mount route (PR 14 of response-formatting design).
 *
 * Mounts `RenderedAssistantMessage` from a query-string fixture name
 * so `tools/e2e/response-formatting/visual-regression.spec.ts` can
 * navigate to a deterministic page and screenshot the result.
 *
 * URL shape: `/__test__/render?fixture=<archetype>-<model>`
 * (matches the Playwright spec's URL builder).
 *
 * Hidden in production: the route is registered only when
 * `import.meta.env.DEV === true`. Production builds tree-shake this
 * file via the dev-only branch in `routerConfig`. There is no
 * sensitive data — fixtures are checked-in JSON — but excluding it
 * from production keeps the public route surface clean.
 */
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { RenderedAssistantMessage } from '@/features/chatV2/render'

interface Fixture {
  model: string
  archetype: string
  expectedParts: unknown[]
}

/**
 * Eager-load every fixture under tests/fixtures/response-formatting/
 * via Vite's `import.meta.glob`. The glob runs at build time so the
 * production bundle excludes this map cleanly when the route is
 * tree-shaken.
 */
const fixtureModules = import.meta.glob<{ default: Fixture }>(
  '../../../../../tests/fixtures/response-formatting/*.json',
  { eager: true },
)

const fixtureLookup: Record<string, Fixture> = Object.entries(fixtureModules).reduce(
  (acc, [path, mod]) => {
    const fileName = path.split('/').pop()?.replace(/\.json$/, '')
    if (!fileName) return acc
    acc[fileName] = mod.default
    // The Playwright URL is "<archetype>-<model>"; the fixture file
    // is also named "<archetype>-<model>.json" — same convention,
    // so a single key works.
    const fx = mod.default
    acc[`${fx.archetype}-${fx.model}`] = fx
    return acc
  },
  {} as Record<string, Fixture>,
)

export function RenderFixture() {
  const [params] = useSearchParams()
  const [fixture, setFixture] = useState<Fixture | null>(null)
  const [error, setError] = useState<string | null>(null)
  const name = params.get('fixture')

  useEffect(() => {
    if (!name) {
      setError('missing ?fixture=<name>')
      return
    }
    const found = fixtureLookup[name]
    if (!found) {
      setError(`fixture "${name}" not found`)
      return
    }
    setFixture(found)
  }, [name])

  if (error) {
    return (
      <div data-testid="render-fixture-error" role="alert">
        {error}
      </div>
    )
  }
  if (!fixture) {
    return <div data-testid="render-fixture-loading">loading…</div>
  }
  return (
    <div style={{ padding: '20px', fontFamily: 'system-ui' }}>
      <header data-testid="render-fixture-meta">
        {fixture.archetype} / {fixture.model}
      </header>
      <RenderedAssistantMessage parts={fixture.expectedParts as never} />
    </div>
  )
}

export default RenderFixture
